import _ from 'lodash'

import { eventEmitter } from '@/event-bus'
import ApplicationFibaBankService from '@/services/retailer/ApplicationFibaBankService.js'
import ApplicationService from '@/services/retailer/ApplicationService.js'
import FarmerService from '@/services/retailer/FarmerService'
import FindeksService from '@/services/retailer/FindeksService.js'
import { errorMessage } from '@/utilities/helpers'

import { FARMER_ERROR_MESSAGES, APPLICATION_ERROR_MESSAGES } from '@/constants/errorMessages'

export const namespaced = true

const getDefaultState = () => {
  return {
    step: 'create',
    tckn: '',
    phone: '',
    business_number: '',
    findeks_phones: [],
    farmer_payment_date: '',
    retailer_payment_release_date: '',
    farmer: null,
    application: null,
    coupon_code: null,
    guarantor: null,
    countries: [],
    errors: []
  }
}

export const state = getDefaultState()

export const mutations = {
  CHANGE_STEP(state, step) {
    state.step = step
  },
  SET_VALUE(state, parameters) {
    state[parameters.type] = parameters.value
  },
  SET_FARMER(state, farmer) {
    state.farmer = farmer
  },
  SET_GUARANTOR(state, guarantor) {
    state.guarantor = guarantor
  },
  SET_FINDEKS_PHONES(state, phones) {
    state.findeks_phones = phones
  },
  SET_BUSINESS_NUMBER(state, business_number) {
    state.business_number = business_number
  },
  SET_APPLICATION(state, application) {
    state.application = application
  },
  ADD_ERROR(state, error) {
    state.errors.push(error)
  },
  SET_ERRORS(state, errors) {
    state.errors = errors
  },
  SET_COUNTRIES(state, countries) {
    state.countries = countries
  },
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  }
}

export const getters = {
  step: state => {
    return state.step
  }
}

export const actions = {
  resetState({ commit }) {
    commit('RESET_STATE')
  },
  addError({ commit, state }, error) {
    const errorExists = _.filter(state.errors, item => {
      return item.type === error.type
    })

    if (errorExists.length === 0) {
      commit('ADD_ERROR', error)
    }
  },
  removeError({ commit, state }, errorType) {
    commit(
      'SET_ERRORS',
      _.filter(state.errors, error => {
        return error.type !== errorType
      })
    )
  },
  changeStep({ commit }, step) {
    commit('CHANGE_STEP', step)
  },
  setValue({ commit }, parameters) {
    commit('SET_VALUE', parameters)
  },
  getFarmerByTckn({ commit }, tckn) {
    return new Promise((resolve, reject) => {
      FarmerService.getFarmerByTckn(tckn)
        .then(response => {
          eventEmitter.emit('removeError', 'farmerByTckn')
          commit('SET_FARMER', response.data)
          commit('SET_BUSINESS_NUMBER', response.data.business_number)
          resolve(response.data)
        })
        .catch(err => {
          eventEmitter.emit('addError', {
            type: 'farmerByTckn',
            message: errorMessage(err)
          })
          reject(err)
        })
    })
  },
  getFindeksPhoneRecords({ commit }, tckn) {
    return new Promise((resolve, reject) => {
      FindeksService.getPhones(tckn)
        .then(response => {
          eventEmitter.emit('removeError', 'findeksPhones')
          commit('SET_FINDEKS_PHONES', response.data)
          resolve(response.data)
        })
        .catch(err => {
          eventEmitter.emit('addError', {
            type: 'findeksPhones',
            message: errorMessage(err)
          })
          reject(err)
        })
    })
  },
  validatePhone({ }, params) {
    return new Promise((resolve, reject) => {
      FarmerService.validatePhone(params.tckn, params.phone)
        .then(response => {
          eventEmitter.emit('removeError', 'validatePhone')
          resolve(response)
        })
        .catch(err => {
          eventEmitter.emit('addError', {
            type: 'validatePhone',
            message: errorMessage(err)
          })
          reject(err)
        })
    })
  },
  createApplication({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.createApplication(params)
        .then(response => {
          commit('SET_APPLICATION', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  /*
  verifyOtp({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.verifyOtp(params.applicationId, Number(params.code))
          .then(response => {
            commit('SET_APPLICATION', response.data)
            resolve(response.data)
          })
          .catch(err => {
            const notification = {
              type: 'danger',
              message: errorMessage(err)
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          })
    })
  },
  */
  completeApplication({ dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.completeApplication(state.application.id, params)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${APPLICATION_ERROR_MESSAGES.completeError} ${errorMessage(err)} ${APPLICATION_ERROR_MESSAGES.checkSalesCompletionCode
              }`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getApplication({ commit, dispatch, state }, applicationId) {
    return new Promise((resolve, reject) => {
      ApplicationService.getApplication(applicationId)
        .then(response => {
          commit('SET_APPLICATION', response.data)
          commit('SET_FARMER', response.data.farmer)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.getApplicationError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  confirmPaymentPlan({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.confirmPaymentPlan(state.application.id)
        .then(response => {
          commit('SET_APPLICATION', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.getApplicationError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFarmer({ commit, dispatch, state }, farmerId) {
    if (state.farmer && farmerId === state.farmer.id) return state.farmer

    return new Promise((resolve, reject) => {
      FarmerService.getFarmer(farmerId)
        .then(response => {
          commit('SET_FARMER', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: FARMER_ERROR_MESSAGES.getFarmerError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  refreshApplication({ commit, dispatch }, applicationId) {
    return new Promise((resolve, reject) => {
      ApplicationService.getApplication(applicationId)
        .then(response => {
          commit('SET_APPLICATION', response.data)
          commit('SET_FARMER', response.data.farmer)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.getApplicationError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateBirthdate({ state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.updateBirthdate(state.application.id, params)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  findeksConfirm({ state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.findeksConfirm(state.application.id, params)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  saveSurveyAnswers({ dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.saveSurveyAnswers(state.application.id, params)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notificationCode = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notificationCode, {
            root: true
          })
          reject(err)
        })
    })
  },
  pinConfirm({ state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.pinConfirm(state.application.id, params)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  validateFibaApplicationHashAndGetFarmerFullname({ dispatch }, hash) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.validateFibaApplicationHashAndGetFarmerFullname(hash)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  confirmFibaApplication({ commit, dispatch, state }, fibaApplicationHash) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.confirmFibaApplication(fibaApplicationHash)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  startUsedProcess({ dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.startUsedProcess(state.application.id, params)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  resendDocumentConfirmationLink({ dispatch }, applicationId) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.resendDocumentConfirmationLink(applicationId)
        .then(response => {
          const notification = {
            type: 'success',
            message: 'Başvuru dokümanları için onay linki başarıyla gönderildi.'
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getCountries({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      ApplicationFibaBankService.getCountries()
        .then(response => {
          commit('SET_COUNTRIES', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
